import { round } from 'utils/formUtils';
import { createDictionary } from 'utils/objectUtils';

import type { Extra } from 'types/ExtrasForm';
import type { RepairOrder } from 'modules/selfServices/types/SelfService';
import { ChecklistSeverityEnum } from 'types/Checklist';

type PricedItem = Pick<Extra | RepairOrder, 'price' | 'discountedPrice' | 'coefficient' | 'severity'>;

/**
 * Price incl. tax, rounded to the cent, as in the BO / FO (`calculateWithVAT`).
 * Without VAT, the price is returned as is (excl. tax amounts are never rounded before the end).
 */
export const computeTax = (price = 0, vat = 0, coefficient = 1) => (
  vat ? round(price * coefficient * (1 + vat / 100)) : price * coefficient
);

/**
 * Discounted price of a line, as in the BO / FO (`computeDiscountedPrice`, Confluence 2.1.6.1):
 * the price and the discount are rounded separately, the discount being `price - discountedPrice` (excl. tax).
 */
export const computeDiscountedPrice = ({ discountedPrice = 0, price = discountedPrice, coefficient = 1 }: PricedItem, vat = 0) => (
  computeTax(price, vat, coefficient) - computeTax(price - discountedPrice, vat)
);

// Reverse of computeTax: retrieves the amount excl. tax (to the cent) from an amount incl. tax
const removeTax = (amountInclTax: number, vat = 0) => round(amountInclTax / (1 + vat / 100));

const roundingForSwitzerland = (amount: number): number => (Math.round(amount * 20) / 20);

// Elsewhere, the amount is rounded to the cent (as `roundNumber` in the FO)
const defaultRounding = (amount: number) => round(amount);

export const roundAmountByCountry = createDictionary<string, typeof roundingForSwitzerland>({
  CH: roundingForSwitzerland,
  LI: roundingForSwitzerland, // Liechtenstein uses the Swiss franc
}, defaultRounding);

// Without VAT, returns the total excl. tax
export const computeTotalPrice = (items: PricedItem[], vat = 0) => (
  items?.reduce((acc, item) => {
    // Exclude items with severity "OFFERED" from total calculation
    if (item.severity === ChecklistSeverityEnum.OFFERED) {
      return acc;
    }
    return acc + computeDiscountedPrice(item, vat);
  }, 0) ?? 0
);

// Always to the cent: the country rounding (e.g. Swiss 0.05) only applies to the total incl. tax
export const computeTotalTax = (totalInclTax: number, totalExclTax: number) => (
  Math.max(0, round(totalInclTax - totalExclTax))
);

type BasketTotalsParams = {
  // One group per category of works (extras, proactive works, repair order), floored at 0 as in the FO
  groups: PricedItem[][];
  vat: number;
  countryCode: string;
  // Amount incl. tax with no breakdown per item (e.g. a pre-authorized amount)
  fixedAmountInclTax?: number;
};

const sumGroups = (groups: PricedItem[][], vat?: number) => (
  groups.reduce((acc, group) => acc + Math.max(0, computeTotalPrice(group, vat)), 0)
);

/**
 * Basket tax breakdown, aligned on the BO / FO (BMWVID-29406, BMWVID-29940, rules validated in BMWVID-29454):
 * - each line incl. tax is computed as in the FO (price and discount rounded separately to the cent);
 * - each category and the total are floored at 0;
 * - the total excl. tax is the sum of the lines excl. tax rounded to the cent, never derived from the rounded total incl. tax;
 * - the tax is the difference to the cent, never negative;
 * - only the total incl. tax is rounded by country (0.05 in CH and LI, BMWVID-29946).
 * These amounts are frozen by the tests of priceUtils.spec.ts (golden master included): change them only on a business request.
 */
export const computeBasketTotals = ({
  groups, vat, countryCode, fixedAmountInclTax = 0,
}: BasketTotalsParams) => {
  const totalInclTax = Math.max(0, sumGroups(groups, vat) + fixedAmountInclTax);
  // Rounded to the cent before computing the tax, so that the displayed excl. tax + tax = total incl. tax
  const totalExclTax = round(Math.max(0, sumGroups(groups) + removeTax(fixedAmountInclTax, vat)));

  return {
    totalExclTax,
    totalTax: computeTotalTax(totalInclTax, totalExclTax),
    total: roundAmountByCountry[countryCode](totalInclTax),
  };
};
